import { useMemo } from 'react';
import { useMyCompanyUser } from './use-my-company-user';
import { useCompanyContext } from '../providers';
import { isTeamAdmin, isTeamGuardian } from '../util/team-roles';

type TeamPermissions = {
    canViewTeam: boolean;
    canEditTeam: boolean;
    canEditDevices: boolean;
    hasSubscription: boolean;
    hasLicense: boolean;
    myUserId: string;
    myCompanyUserId: string;
};

const defaultPermissions: TeamPermissions = {
    canViewTeam: false,
    canEditTeam: false,
    canEditDevices: false,
    hasSubscription: false,
    hasLicense: false,
    myUserId: '',
    myCompanyUserId: ''
};

export function useTeamPermissions(params: {
    teamId: string;
}): TeamPermissions {
    const { teamId } = params;
    const myCompanyUser = useMyCompanyUser();
    const { company } = useCompanyContext();

    const roles = useMemo(() => {
        if (!myCompanyUser) {
            return [];
        }

        const roles: (
            | 'owner'
            | 'admin'
            | 'billing'
            | 'member'
            | 'team_admin'
            | 'team_guardian'
            | 'team_member'
        )[] = [myCompanyUser.role];

        const team = myCompanyUser.teams.find((team) => team.teamId === teamId);
        if (team) {
            roles.push('team_member');
            if (isTeamAdmin(team)) {
                roles.push('team_admin');
            }
            if (isTeamGuardian(team)) {
                roles.push('team_guardian');
            }
        }
        return roles;
    }, [myCompanyUser, teamId]);

    const hasSubscription = company.subscription !== null;
    const hasLicense = !!myCompanyUser?.license;

    const canViewTeam = true;
    const canEditTeam = useMemo(() => {
        return roles.some((role) =>
            ['owner', 'admin', 'team_admin'].includes(role)
        );
    }, [roles]);
    // Team admins (and company owners/admins) can edit devices within this
    // team's devices tab.
    const canEditDevices = canEditTeam;

    if (!myCompanyUser) {
        return defaultPermissions;
    } else {
        return {
            canViewTeam,
            canEditTeam,
            canEditDevices,
            hasSubscription,
            hasLicense,
            myUserId: myCompanyUser.userId ?? '',
            myCompanyUserId: myCompanyUser.id
        };
    }
}
